/**
 * Editing Section Banner
 *
 * A dismissable banner that appears in the chat sidebar when a user
 * is focused on editing a specific section. Shows which section
 * is currently being edited.
 */

import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Fade, IconButton, Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import { clearFocusedSection } from '../store/chatSlice';
import { selectFocusedSection } from '../store/selectors';

/**
 * Banner component that shows "Editing [section name]" when a section is focused.
 * Appears above the chat input and can be dismissed.
 * Always reserves space to prevent layout shift.
 */
export const EditingSectionBanner: React.FC = () => {
  const dispatch = useAppDispatch();
  const focusedSection = useAppSelector(selectFocusedSection);

  const handleDismiss = () => {
    dispatch(clearFocusedSection());
  };

  // Always render container with fixed height to prevent layout shift
  // Height: py (8px top + 8px bottom) + content (~32px) + mb (8px) = ~48px
  // Using fixed height ensures no layout shift when banner appears/disappears
  const BANNER_HEIGHT = 48; // Approximate height: py (16px) + content (~24px) + mb (8px)
  
  return (
    <Box
      sx={{
        height: focusedSection ? BANNER_HEIGHT : 0,
        px: 2,
        mb: focusedSection ? 1 : 0,
        overflow: 'hidden',
        transition: 'height 0.15s ease, margin-bottom 0.15s ease',
      }}
    >
      <Fade in={!!focusedSection} timeout={150}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 2,
            py: 1,
            mx: 0,
            backgroundColor: 'rgba(77, 50, 215, 0.08)',
            borderRadius: '8px',
            border: '1px solid',
            borderColor: 'rgba(77, 50, 215, 0.2)',
          }}
        >
          {focusedSection ? (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <EditIcon
                  sx={{
                    fontSize: 16,
                    color: 'primary.main',
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: 13,
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Editing{' '}
                  <Box
                    component="span"
                    sx={{
                      color: 'primary.main',
                      fontWeight: 600,
                    }}
                  >
                    {focusedSection.name}
                  </Box>
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={handleDismiss}
                aria-label="Dismiss editing banner"
                sx={{
                  p: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </>
          ) : null}
        </Box>
      </Fade>
    </Box>
  );
};

EditingSectionBanner.displayName = 'EditingSectionBanner';

